import { useEffect, useMemo, useState } from "react";
import { RadialBar, RadialBarChart, PolarAngleAxis, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip } from "recharts";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Banknote, Loader2, RefreshCw, CheckCircle2, AlertTriangle, ChevronRight,
  ArrowRight, Zap, ListChecks, Target,
} from "lucide-react";
import { api } from "@/lib/api";
import { Link } from "react-router-dom";
import VaultEvidenceMap from "@/components/VaultEvidenceMap";

/**
 * Bankability engine — dossier-scoped section.
 *   • Gauge (0..100) + grade badge
 *   • 6 sub-scores as horizontal bars
 *   • Prioritized blockers (impact/effort ratio) with action plans
 *   • Dynamic checklist with completion pct
 */

const SUB_LABELS = {
  preparation: "Préparation dossier",
  capacity:    "Capacité de remboursement",
  collateral:  "Garanties & actifs",
  debt:        "Endettement",
  history:     "Historique de crédit",
  coherence:   "Cohérence du projet",
};

function gradeColor(g) {
  const x = (g || "").toUpperCase();
  if (x === "A") return "#059669";
  if (x === "B") return "#65A30D";
  if (x === "C") return "#C5A880";
  if (x === "D") return "#D97706";
  return "#B91C1C";
}

function barTone(v) {
  if (v >= 75) return "bg-emerald-500";
  if (v >= 60) return "bg-lime-500";
  if (v >= 45) return "bg-[#C5A880]";
  if (v >= 30) return "bg-amber-500";
  return "bg-red-500";
}

export function DossierBankabilitySection({ dossier, onDossierUpdate }) {
  const dossierId = dossier?.dossier_id;
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [report, setReport] = useState(null);

  const load = async (compute = false) => {
    if (!dossierId) return;
    setLoading(!compute);
    if (compute) setRunning(true);
    try {
      const endpoint = compute
        ? `/bankability/dossier/${dossierId}/compute`
        : `/bankability/dossier/${dossierId}/report`;
      const { data } = await (compute ? api.post(endpoint) : api.get(endpoint));
      setReport(data);
      if (compute) {
        toast.success(`Bancabilité calculée — ${data.bankability?.index}/100 (${data.bankability?.grade}).`);
        onDossierUpdate?.();
      }
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Impossible de calculer la bancabilité.");
    } finally { setLoading(false); setRunning(false); }
  };

  useEffect(() => { load(false); /* eslint-disable-next-line */ }, [dossierId]);

  const b = report?.bankability;
  const chk = report?.checklist;
  const barData = useMemo(() => {
    if (!b?.sub_scores) return [];
    return Object.entries(b.sub_scores).map(([k, v]) => ({
      key: k, label: SUB_LABELS[k] || k, value: Number(v || 0),
    }));
  }, [b]);

  if (loading && !b) {
    return (
      <section className="mt-12" data-testid="dossier-bankability-section">
        <SectionHeader />
        <div className="mt-6 rounded-2xl border border-[#E4E1D8] bg-white p-8 flex items-center justify-center text-[#5B5B62] text-sm">
          <Loader2 className="h-4 w-4 animate-spin mr-2" /> Chargement…
        </div>
      </section>
    );
  }

  if (!b) return null;

  const idx = Number(b.index || 0);
  const color = gradeColor(b.grade);

  return (
    <section className="mt-12 space-y-6" data-testid="dossier-bankability-section">
      <SectionHeader
        subtitle={`Méthode ${b.method_version} · ${b.client_type}`}
        actions={
          <>
            <button onClick={() => load(true)} disabled={running}
                    data-testid="dossier-bankability-recompute"
                    className="inline-flex items-center gap-1.5 rounded-md border border-[#E4E1D8] bg-white hover:border-[#0B2545] hover:text-[#0B2545] disabled:opacity-60 text-[#1F2937] px-3 py-2 text-xs font-medium transition-colors">
              {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <RefreshCw className="h-3 w-3" />}
              {running ? "Calcul…" : "Recalculer"}
            </button>
            <Link to={`/app/dossiers/${dossierId}/carriere`}
                  data-testid="dossier-bankability-goto-careers"
                  className="inline-flex items-center gap-1.5 rounded-md border border-[#7A5B14]/40 bg-[#FBF6EA] hover:bg-[#F1E9D0] text-[#7A5B14] px-3 py-2 text-xs font-medium transition-colors">
              Découverte carrière <ArrowRight className="h-3 w-3" />
            </Link>
            <Link to={`/app/dossiers/${dossierId}/opportunites`}
                  data-testid="dossier-bankability-goto-opps"
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#0B2545] hover:bg-[#123163] text-white px-3 py-2 text-xs font-medium transition-colors">
              Opportunités <ArrowRight className="h-3 w-3" />
            </Link>
          </>
        }
      />

      {/* Hero: gauge + sub-scores */}
      <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
                  className="rounded-2xl border-2 border-[#0B2545]/25 bg-white shadow-sm overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-6 p-6 md:p-8">
          <div className="flex flex-col items-center">
            <div className="relative">
              <RadialBarChart width={240} height={240} cx="50%" cy="50%" innerRadius="76%" outerRadius="100%"
                              barSize={14} startAngle={90} endAngle={-270}
                              data={[{ name: "b", value: Math.max(0, Math.min(100, idx)) }]}>
                <PolarAngleAxis type="number" domain={[0, 100]} angleAxisId={0} tick={false} />
                <RadialBar background={{ fill: "#EBE8DE" }} dataKey="value" cornerRadius={4} fill={color} />
              </RadialBarChart>
              <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                <div className="font-mono text-5xl font-medium text-[#1F2937] tabular-nums"
                     data-testid="dossier-bankability-index">{idx.toFixed(1)}</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.24em] text-[#5B5B62]">/ 100</div>
                {b.grade && (
                  <div className="mt-2 rounded-full px-2.5 py-0.5 text-[10px] font-mono uppercase tracking-widest"
                       style={{ backgroundColor: `${color}18`, color }} data-testid="dossier-bankability-grade">
                    Grade {b.grade}
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 text-center text-xs text-[#5B5B62] max-w-[240px]">
              Indice composite pondéré sur 6 axes déterministes. Réplicable à 100 %.
            </div>
          </div>

          {/* Sub-scores as horizontal bars */}
          <div>
            <h3 className="font-display text-lg text-[#0B2545] mb-1">6 axes de bancabilité</h3>
            <p className="text-xs text-[#5B5B62] mb-4">
              Chaque axe est pondéré ({Object.entries(b.weights || {}).map(([k, w]) => `${SUB_LABELS[k]?.split(" ")[0] || k} ${(w * 100).toFixed(0)}%`).join(" · ")}).
            </p>
            <div className="space-y-3" data-testid="dossier-bankability-sub-scores">
              {barData.map((row) => (
                <div key={row.key}>
                  <div className="flex items-center justify-between text-xs mb-1">
                    <span className="text-[#1F2937] font-medium">{row.label}</span>
                    <span className="text-[#5B5B62] font-mono tabular-nums">{row.value.toFixed(1)}</span>
                  </div>
                  <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                    <div className={`h-full ${barTone(row.value)} transition-all`} style={{ width: `${Math.max(2, row.value)}%` }} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Blockers + Wins + Checklist */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Blockers */}
        <div className="border-2 border-[#E5E7EB] bg-white rounded-lg p-6" data-testid="dossier-bankability-blockers">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="h-4 w-4 text-[#B91C1C]" strokeWidth={2} />
            <h3 className="font-display text-lg text-[#0B2545]">Plan d'amélioration priorisé</h3>
            <span className="ml-auto text-[10px] font-mono opacity-60">{b.blockers?.length || 0}</span>
          </div>
          {(!b.blockers || b.blockers.length === 0) ? (
            <div className="text-center py-6 text-[#5B5B62] text-sm">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2 text-emerald-600" strokeWidth={1.5} />
              Aucun bloqueur majeur détecté — dossier prêt pour la présentation.
            </div>
          ) : (
            <ul className="space-y-4">
              {b.blockers.map((blk, i) => (
                <li key={blk.axis + i} className="border-l-4 border-[#D97706] bg-[#FEF3C7]/30 rounded-r-md px-3 py-2"
                    data-testid={`dossier-bankability-blocker-${blk.axis}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-display text-sm text-[#0B2545]">{blk.title_fr}</div>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-800 whitespace-nowrap">
                      +{blk.impact_points} pts · {blk.effort_weeks}sem
                    </div>
                  </div>
                  <p className="text-xs text-[#5B5B62] mt-1 leading-relaxed">{blk.detail_fr}</p>
                  <ul className="mt-2 space-y-0.5 text-xs text-[#1F2937]">
                    {(blk.next_steps_fr || []).map((s, k) => (
                      <li key={k} className="flex gap-2"><ChevronRight className="h-3 w-3 opacity-40 mt-0.5" /><span>{s}</span></li>
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          )}
          {b.wins?.length > 0 && (
            <div className="mt-6 pt-4 border-t border-[#E4E1D8]">
              <div className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 mb-2">Points forts déjà acquis</div>
              <ul className="space-y-1">
                {b.wins.map((w, i) => (
                  <li key={i} className="flex items-start gap-2 text-xs text-[#065F46]">
                    <CheckCircle2 className="h-3.5 w-3.5 mt-0.5" strokeWidth={2} />
                    <span><span className="font-medium">{w.title_fr}.</span> <span className="opacity-80">{w.detail_fr}</span></span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Checklist */}
        <div className="border-2 border-[#E5E7EB] bg-white rounded-lg p-6" data-testid="dossier-bankability-checklist">
          <div className="flex items-center gap-2 mb-4">
            <ListChecks className="h-4 w-4 text-[#0B2545]" strokeWidth={2} />
            <h3 className="font-display text-lg text-[#0B2545]">Checklist intelligente</h3>
            <span className="ml-auto text-[10px] font-mono opacity-60">
              {chk?.stats?.done || 0} / {chk?.stats?.total || 0}
            </span>
          </div>
          {chk?.stats && (
            <div className="mb-4">
              <div className="h-2 bg-[#F3F4F6] rounded-full overflow-hidden">
                <div className="h-full bg-emerald-500 transition-all" style={{ width: `${chk.stats.pct}%` }} />
              </div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#5B5B62] mt-1">
                {chk.stats.pct}% complété · profil {chk.template_id}
              </div>
            </div>
          )}
          <ul className="space-y-2">
            {(chk?.items || []).map((item) => (
              <li key={item.id}
                  className={`flex items-start gap-2 p-2 rounded ${item.status === "done" ? "bg-emerald-50/50" : "bg-[#F8F7F3]/50"}`}
                  data-testid={`dossier-checklist-${item.id}`}>
                {item.status === "done"
                  ? <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0 mt-0.5" strokeWidth={2} />
                  : <div className={`h-4 w-4 rounded-full border-2 flex-shrink-0 mt-0.5 ${item.required ? "border-red-400" : "border-[#C5A880]"}`} />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-[#1F2937]">
                    {item.title_fr}
                    {!item.required && <span className="ml-1 text-[9px] font-mono uppercase text-[#8B8B8F]">(optionnel)</span>}
                  </div>
                  {item.detail_fr && (
                    <div className="text-[11px] text-[#5B5B62] mt-0.5 leading-relaxed">{item.detail_fr}</div>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Vault evidence map — cite the documents that support each sub-score */}
      <VaultEvidenceMap dossierId={dossier?.dossier_id} engine="bankability" />
    </section>
  );
}

function SectionHeader({ subtitle, actions }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
      <div>
        <div className="text-[11px] font-mono uppercase tracking-[0.28em] text-[#7A5B14]">Bancabilité ALTIORA™</div>
        <h2 className="mt-1 font-display text-2xl tracking-tight text-[#1F2937]">Indice de préparation dossier</h2>
        {subtitle && <div className="mt-1 text-[10px] font-mono uppercase tracking-widest text-[#5B5B62]">{subtitle}</div>}
      </div>
      {actions && <div className="flex items-center gap-2 flex-wrap">{actions}</div>}
    </div>
  );
}

export default DossierBankabilitySection;
